import clsx from 'clsx';
import React from 'react';

import userImg from '../../../../assets/user.png';
import greenTick from '../../../../assets/green-tick.svg';
import linkedin from '../../../../assets/linkedin.svg';
import CalendarIcon from '../../../../assets/Svg/CalendarIcon';
import { alertIcon, ctaPurpleRight } from '../../../../assets/hostedassets';
import { isEmpty, isSafeUrl, toTitleCase } from '../../../../utils/utils';
import { DATE_FORMATS, formatAgeLabel, formatDateByFormat, formatTenureYears } from '../../../../utils/dateUtils';
import { KEYBOARD_EVENTS, TabModule } from '../../../../utils/constants/commonConstants';
import { formatShareholdingPercent, TINY_VALUE_FLOOR } from '../../../../utils/formatShareholdingPercent';
import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';
import { BureauType } from '../CreditBureau/components/Report/CreditBureauConstants';
import {
    NOT_CHECKED_TOOLTIP,
    REGULATORY_CLICK_TOOLTIP,
    REGULATORY_NO_DATA_TOOLTIP,
    RISK_STATE_CONFIG,
    RegulatoryRiskState,
} from '../../../RiskIndicators/RiskIndicatorConstants';
import PotentialRiskRedirectIcon from '../../../RiskIndicators/PotentialRiskRedirectIcon';
import { PillCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/PillCellRenderer/PillCellRenderer';
import { CellRendererProps } from '../../../../common/_custom/DataTable/DataTable.types';
// MANAGEMENT_PROFILE_COMPONENT_FIELDS: dispatched on by the COMPONENT renderer registry below.
// The polus seed sets no `componentName` on any of these columns (D6), so dispatch is on
// `column.field` — a `componentName` switch would silently render blank (pattern:
// GstinTable.tsx:263-278).
import {
    ColumnLockKey,
    MANAGEMENT_PROFILE_COMPONENT_FIELDS,
    NO_ALERTS_LABEL,
    RISK_ALERTS_STATE,
    RISK_ALERTS_TOOLTIPS,
} from './ManagementProfileTabConstants';
import {
    formatAlertCount,
    isCheckNotConfigured,
    resolveDirectorLegalCases,
    resolveRegulatoryCheck,
    resolveRiskAlertsState,
} from './checkStatusResolvers';
import type { RegulatoryCheckDisplay } from './types';
import { LegalCasesStateLabel, NoDataLabel, NotCheckedLabel, NotConfiguredCell, WithHoverTip } from './CheckStatusLabels';
import type { Direction } from '../../components/DashboardTables/LegalCases/legalCasesType';
import { LEGAL_DIRECTION, LEGAL_DIRECTION_PARAM } from '../../components/DashboardTables/LegalCases/legalCasesConstants';
import { DirectorTableRow } from './useManagementProfileTabLogic';
import { phoneValueGetter } from './managementProfileValueGetters';
import styles from './ManagementProfileTab.module.scss';
import DirectorHoverCardTooltip from '../RelatedParties/components/DirectorHoverCard/DirectorHoverCardTooltip';
import { toDirectorHoverProfile } from '../RelatedParties/components/DirectorHoverCard/toDirectorHoverProfile';
import { DirectorStatusPill } from '../../../../common/DirectorStatus/DirectorStatus';

function NameCell({ row }: { row: DirectorTableRow }) {
    const ageLabel = formatAgeLabel(row.age);
    return (
        <DirectorHoverCardTooltip profile={toDirectorHoverProfile(row)}>
            <div className={styles.nameCell}>
                <div className={styles.avatar}>
                    <img
                        src={row.imageUrl || userImg}
                        alt={row.name}
                        onError={({ currentTarget }) => {
                            currentTarget.onerror = null;
                            currentTarget.src = userImg;
                        }}
                    />
                </div>
                <div className={styles.nameInfo}>
                    <div className={styles.NameRow}>
                        {/* A director added by PAN has no name until the enrichment lands — and none
                            at all if it finds no match — so the cell falls back like every other
                            empty one here (same dash DirectorHoverCard shows for the same row). */}
                        <span className={styles.directorName}>{toTitleCase(row.name) || '-'}</span>
                        {row.linkedinUrl && isSafeUrl(row.linkedinUrl) && (
                            <a
                                href={row.linkedinUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.LinkedInMark}
                                aria-label={`${row.name} on LinkedIn`}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <img src={linkedin} alt="" />
                            </a>
                        )}
                    </div>
                    {ageLabel && (
                        <span className={styles.AgeRow}>
                            <CalendarIcon className={styles.AgeIcon} />
                            {ageLabel}
                        </span>
                    )}
                </div>
            </div>
        </DirectorHoverCardTooltip>
    );
}

function ShareholdingCell({ row }: { row: DirectorTableRow }) {
    // Standard data-cell styling (matches Tenure/Contact) — not .AgeRow, which is the
    // small grey secondary-label style meant for the "34 years" line under a name.
    const value = row.shareholdingPercentage;
    const formatted = formatShareholdingPercent(value, { nullText: '-' });
    const cell = <span className={styles.tenureYears}>{formatted}</span>;

    // Below the tiny-value floor the display collapses to "<0.0001%" — a tooltip with the
    // exact raw value is the only way to see what it actually is. Every other tier already
    // shows enough precision to be self-explanatory, so the tooltip is scoped to just this case.
    const isTinyCollapsed = value != null && Number.isFinite(value) && value > 0 && value < TINY_VALUE_FLOOR;
    if (!isTinyCollapsed) return cell;

    return (
        <Tooltip content={`${value}%`} direction="top" contentCss={{ whiteSpace: 'nowrap' }} fitContent small usePortal>
            {cell}
        </Tooltip>
    );
}

function DesignationCell({ row }: { row: DirectorTableRow }) {
    // Plain TEXT columns render via the shared DataTable's default (muted) text color —
    // route this one through the COMPONENT registry instead so it matches the other data
    // columns (Status/Tenure/Shareholding), same styles.tenureYears black used there.
    return <span className={styles.tenureYears}>{row.designation || '-'}</span>;
}

function TenureCell({ row }: { row: DirectorTableRow }) {
    if (!row.tenureBeginDate) return <span className={styles.cellDash}>-</span>;
    return (
        <div className={styles.tenureInfo}>
            <span className={styles.tenureYears}>{formatTenureYears(row.tenureBeginDate, row.tenureEndDate)}</span>
            <span className={styles.tenureDates}>
                ({formatDateByFormat(row.tenureBeginDate, DATE_FORMATS.MONTH_SHORT_YEAR)} -{' '}
                {row.tenureEndDate ? formatDateByFormat(row.tenureEndDate, DATE_FORMATS.MONTH_SHORT_YEAR) : row.isActive ? 'Present' : ''})
            </span>
        </div>
    );
}

function ContactCell({ row }: { row: DirectorTableRow }) {
    if (row.loading) return <span className={styles.cellDash}>-</span>;
    // Same masking rule as the standalone Phone column: a masked vendor value is not the
    // director's number, so this column must not present it as one.
    const phone = phoneValueGetter(row);
    if (!row.email && !phone) return <span className={styles.cellDash}>-</span>;
    return (
        <div className={styles.contactInfo}>
            {row.email && <span className={styles.contactEmail}>{row.email}</span>}
            {phone && <span className={styles.contactPhone}>{phone}</span>}
        </div>
    );
}

// Enter/Space activation for the click-through cells below — they're plain `div`s (not
// `<button>`), so keyboard/screen-reader users need an explicit role + key handler.
function handleActivationKeyDown(e: React.KeyboardEvent<HTMLDivElement>, onNavigate: () => void) {
    if (e.key === KEYBOARD_EVENTS.ENTER || e.key === KEYBOARD_EVENTS.SPACE) {
        e.preventDefault();
        e.stopPropagation();
        onNavigate();
    }
}

/** Risk Alerts: MCA alerts from Structure & Relations, in the states that tab itself shows. */
function RiskAlertsCell({ row, locked, onNavigate }: { row: DirectorTableRow; locked: boolean; onNavigate: () => void }) {
    const state = locked || row.loading ? null : resolveRiskAlertsState(row);
    const count = row.riskIndicatorCount ?? 0;
    switch (state) {
        case RISK_ALERTS_STATE.ALERTS:
            return (
                <WithHoverTip tooltip={RISK_ALERTS_TOOLTIPS.ALERTS} focusable={false}>
                    <div
                        className={styles.alertClickable}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                            e.stopPropagation();
                            onNavigate();
                        }}
                        onKeyDown={(e) => handleActivationKeyDown(e, onNavigate)}
                    >
                        <span className={styles.alertRed}>
                            <img src={alertIcon} alt="" className={styles.CountIcon} />
                            {formatAlertCount(count)}
                        </span>
                    </div>
                </WithHoverTip>
            );
        case RISK_ALERTS_STATE.NO_ALERTS:
            return (
                <WithHoverTip tooltip={RISK_ALERTS_TOOLTIPS.NO_ALERTS}>
                    <span className={styles.alertGrey}>
                        <img src={greenTick} alt="" className={styles.CountIcon} />
                        {NO_ALERTS_LABEL}
                    </span>
                </WithHoverTip>
            );
        case RISK_ALERTS_STATE.NOT_AVAILABLE:
            return (
                <WithHoverTip tooltip={RISK_ALERTS_TOOLTIPS.NOT_AVAILABLE}>
                    <NoDataLabel />
                </WithHoverTip>
            );
        case RISK_ALERTS_STATE.NOT_CHECKED:
            return (
                <WithHoverTip tooltip={RISK_ALERTS_TOOLTIPS.NOT_CHECKED}>
                    <NotCheckedLabel />
                </WithHoverTip>
            );
        default:
            return <span className={styles.cellDash}>-</span>;
    }
}

/*
    RECUR-27829. The Regulatory Checks badge has its own cell applying the SAME rule as the
    Legal & Regulatory table: the confirmed count when there is one, otherwise the probable count,
    in that table's own colours. RCPI-10463 was opened because the two surfaces disagreed.

    Deliberately NOT folded into the Risk Alerts cell, which has its own red/grey states and must
    not grow an amber branch it never uses.
*/
function RegulatoryChecksCell({ row, locked, onNavigate }: { row: DirectorTableRow; locked: boolean; onNavigate: () => void }) {
    if (isCheckNotConfigured(row, 'regulatoryChecks', locked)) return <NotConfiguredCell />;
    const display: RegulatoryCheckDisplay = locked || row.loading ? { kind: 'dash' } : resolveRegulatoryCheck(row);
    switch (display.kind) {
        case 'badge': {
            const { count, label, color, backgroundColor } = display.badge;
            return (
                <WithHoverTip tooltip={REGULATORY_CLICK_TOOLTIP} focusable={false}>
                    <div
                        className={styles.alertClickable}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                            e.stopPropagation();
                            onNavigate();
                        }}
                        onKeyDown={(e) => handleActivationKeyDown(e, onNavigate)}
                    >
                        <span
                            className={styles.alertBadge}
                            // The hover wash comes from here: .alertClickable is shared with the
                            // always-red Risk Alerts badge, so a stylesheet hover tinted Probable pink.
                            style={{ color, ['--badge-hover-bg' as string]: backgroundColor } as React.CSSProperties}
                        >
                            <PotentialRiskRedirectIcon className={styles.regulatoryBadgeIcon} color={color} />
                            {`${count} ${label}`}
                        </span>
                    </div>
                </WithHoverTip>
            );
        }
        case 'noMatch':
            return (
                <span className={styles.alertGrey}>
                    <img src={greenTick} alt="" className={styles.CountIcon} />
                    {RISK_STATE_CONFIG[RegulatoryRiskState.NO_RISK].label}
                </span>
            );
        case 'noData':
            return (
                <WithHoverTip tooltip={REGULATORY_NO_DATA_TOOLTIP}>
                    <NoDataLabel />
                </WithHoverTip>
            );
        case 'notChecked':
            return (
                <WithHoverTip tooltip={NOT_CHECKED_TOOLTIP}>
                    <NotCheckedLabel />
                </WithHoverTip>
            );
        default:
            return <span className={styles.cellDash}>-</span>;
    }
}

// Filed Against before polus sends legalCases: the old summed count, as before.
function LegacyLegalCasesCountCell({ row, onNavigate }: { row: DirectorTableRow; onNavigate: () => void }) {
    const count = row.legalCasesCount;
    const clickable = !!count;
    return (
        <div
            className={clsx({ [styles.alertClickable]: clickable })}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={
                clickable
                    ? (e) => {
                          e.stopPropagation();
                          onNavigate();
                      }
                    : undefined
            }
            onKeyDown={clickable ? (e) => handleActivationKeyDown(e, onNavigate) : undefined}
        >
            {count == null ? (
                <span className={styles.cellDash}>-</span>
            ) : count === 0 ? (
                <span className={styles.alertGrey}>
                    <img src={greenTick} alt="" className={styles.CountIcon} />
                    No match found
                </span>
            ) : (
                <span className={styles.alertRed}>
                    <img src={alertIcon} alt="" height="14px" />
                    {`${count} ${count === 1 ? 'Case' : 'Cases'}`}
                </span>
            )}
        </div>
    );
}

// The two Legal Cases columns: the existing legalCasesCount column is Filed Against.
const LEGAL_DIRECTION_BY_FIELD: Record<string, Direction> = {
    [MANAGEMENT_PROFILE_COMPONENT_FIELDS.LEGAL_CASES_COUNT]: LEGAL_DIRECTION.FILED_AGAINST,
    [MANAGEMENT_PROFILE_COMPONENT_FIELDS.LEGAL_CASES_FILED_BY]: LEGAL_DIRECTION.FILED_BY,
};

/** Legal Cases Against / Filed By, in the states and tooltips of the Legal & Regulatory table. */
function LegalCasesCell({
    row,
    locked,
    direction,
    onNavigate,
}: {
    row: DirectorTableRow;
    locked: boolean;
    direction: Direction;
    onNavigate: () => void;
}) {
    if (isCheckNotConfigured(row, 'legalCases', locked)) return <NotConfiguredCell />;
    if (locked || row.loading) return <span className={styles.cellDash}>-</span>;
    if (row.legalCases === undefined && direction === LEGAL_DIRECTION.FILED_AGAINST)
        return <LegacyLegalCasesCountCell row={row} onNavigate={onNavigate} />;
    const state = resolveDirectorLegalCases(row, direction);
    if (!state) return <span className={styles.cellDash}>-</span>;
    const label = <LegalCasesStateLabel state={state} />;
    const content = state.openable ? (
        <div
            className={styles.alertClickable}
            role="button"
            tabIndex={0}
            onClick={(e) => {
                e.stopPropagation();
                onNavigate();
            }}
            onKeyDown={(e) => handleActivationKeyDown(e, onNavigate)}
        >
            {label}
        </div>
    ) : (
        label
    );
    return state.tooltip ? (
        <WithHoverTip tooltip={state.tooltip} focusable={!state.openable}>
            {content}
        </WithHoverTip>
    ) : (
        content
    );
}

function CreditScoreCell({ row, onNavigate }: { row: DirectorTableRow; onNavigate: () => void }) {
    const isClickable = !row.loading && typeof row.creditScore === 'number' && !Number.isNaN(row.creditScore);
    // Not Configured wins over a stored score, as the Credit Bureau tab drops excluded designations.
    if (isCheckNotConfigured(row, 'creditBureau')) return <NotConfiguredCell />;
    return (
        <div
            className={clsx({ [styles.creditClickable]: isClickable })}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onClick={
                isClickable
                    ? (e) => {
                          e.stopPropagation();
                          onNavigate();
                      }
                    : undefined
            }
            onKeyDown={isClickable ? (e) => handleActivationKeyDown(e, onNavigate) : undefined}
        >
            {isClickable ? (
                <span className={styles.creditScore}>
                    <img src={ctaPurpleRight} alt="" height="14px" />
                    {row.creditScore}
                </span>
            ) : (
                <span className={styles.cellDash}>-</span>
            )}
        </div>
    );
}

interface ManagementProfileComponentRegistryDeps {
    columnLocks: Record<ColumnLockKey, boolean>;
    navigateToTab?: (tabId: string, additionalParams?: Record<string, string>) => void;
}

/**
 * Builds the `componentRenderFnRegistry` handed to `ComponentCellRenderer` for every
 * `COMPONENT`-typed Management Profile column. Lock state and the navigation callback are
 * captured by closure (not via `show`) — column locks gate click-through/cell content, they
 * never hide the column (D6).
 */
export function createManagementProfileComponentRegistry({ columnLocks, navigateToTab }: ManagementProfileComponentRegistryDeps) {
    return function managementProfileComponentRenderFnRegistry({ row, column }: { row: DirectorTableRow; column?: any }) {
        switch (column?.field) {
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.NAME:
                return <NameCell row={row} />;
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.TENURE:
                return <TenureCell row={row} />;
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.CONTACT:
                return <ContactCell row={row} />;
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.SHAREHOLDING:
                return <ShareholdingCell row={row} />;
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.DESIGNATION:
                return <DesignationCell row={row} />;
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.RISK_INDICATOR_COUNT:
                return (
                    <RiskAlertsCell
                        row={row}
                        locked={columnLocks.riskAlerts}
                        onNavigate={() =>
                            navigateToTab?.(TabModule.RELATED_PARTY, { directorName: row.name ?? '', directorDin: row.din ?? '' })
                        }
                    />
                );
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.AML_PEP_RISK_COUNT:
                return (
                    <RegulatoryChecksCell
                        row={row}
                        locked={columnLocks.regulatoryChecks}
                        onNavigate={() =>
                            navigateToTab?.(TabModule.LEGAL_CHECKS, {
                                directorName: row.name ?? '',
                                directorDin: row.din ?? '',
                                section: 'regulatoryChecks',
                            })
                        }
                    />
                );
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.LEGAL_CASES_COUNT:
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.LEGAL_CASES_FILED_BY: {
                const direction = LEGAL_DIRECTION_BY_FIELD[column.field];
                return (
                    <LegalCasesCell
                        row={row}
                        locked={columnLocks.legalCases}
                        direction={direction}
                        onNavigate={() =>
                            navigateToTab?.(TabModule.LEGAL_CHECKS, {
                                directorName: row.name ?? '',
                                directorDin: row.din ?? '',
                                [LEGAL_DIRECTION_PARAM]: direction,
                            })
                        }
                    />
                );
            }
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.CREDIT_SCORE:
                return (
                    <CreditScoreCell
                        row={row}
                        onNavigate={() =>
                            navigateToTab?.(TabModule.CREDIT_BUREAU, {
                                bureauType: BureauType.ALL,
                                directorName: row.name ?? '',
                                focusDirectorId: row.directorId ?? '',
                            })
                        }
                    />
                );
            default:
                return null;
        }
    };
}

// PILL adapter — PillCellRenderer takes `{label, isActive}`, not `CellRendererProps`
// (pattern: OrgBasedUsersTable.tsx:401-405). `isActive` keeps its bespoke Active/Former
// copy (the system Status column); any other PILL column (admin-created custom field,
// G-5) falls back to a generic boolean pill so it renders something instead of going
// blank — same isEmpty→dash / truthy→pill convention used by every other ad hoc PILL
// renderer in this repo (OrgBasedUsersTable.tsx, MandateSharingDrawer.tsx).
export function ManagementProfileStatusPillRenderer({ value, column }: CellRendererProps) {
    if (column?.field === 'isActive') {
        if (value === null || value === undefined) return <span className={styles.cellDash}>-</span>;
        // Delegates to the shared DirectorStatusPill (Regulatory Checks/Credit Bureau use the
        // same decision).
        return <DirectorStatusPill isActive={value} />;
    }
    if (isEmpty(value)) return <span className={styles.cellDash}>-</span>;
    return <PillCellRenderer label={value ? 'Yes' : 'No'} isActive={!!value} />;
}
